import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';

// Support requests (supervisor, waste tipper) asked for from this phone, kept
// until the server has them, so a request shows on the job at once with or
// without signal.
const KEY = 'nu.support';
const listeners = new Set();
let cache = null;

function readAll() {
  if (cache) return cache;
  try { cache = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { cache = []; }
  if (!Array.isArray(cache)) cache = [];
  return cache;
}
function writeAll(list) {
  // Keep a week.
  cache = list.filter((r) => Date.now() - Date.parse(r.requestedAt) < 7 * 864e5);
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* lasts this visit */ }
  listeners.forEach((fn) => fn());
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// Ask for support (queued if there's no signal).
export async function requestSupport(job, { type, notes }) {
  const id = crypto.randomUUID();
  const at = new Date().toISOString();
  writeAll([...readAll(), { id, bookingId: job.id, type, notes, requestedAt: at, local: true }]);
  await outbox.enqueue('job.support', { id, bookingId: job.id, type, notes, at });
  return id;
}

// A job's requests: the server's (kept on the phone for no signal) plus any
// from this phone the server doesn't have yet.
export function useJobSupport(bookingId) {
  const key = `nu.jobSupport.${bookingId}`;
  const local = useSyncExternalStore(subscribe, readAll);
  const [server, setServer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  });
  useEffect(() => {
    let live = true;
    const load = () => api(`/my/jobs/${bookingId}/support`).then((list) => {
      if (!live) return;
      setServer(list);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* fine */ }
    }, () => {});
    load();
    const off = outbox.subscribe(load);
    return () => { live = false; off(); };
  }, [bookingId, key]);
  const known = new Set(server.map((r) => r.id));
  return [...server, ...local.filter((r) => r.bookingId === bookingId && !known.has(r.id))]
    .sort((a, b) => String(b.requestedAt).localeCompare(String(a.requestedAt)));
}
